// src/components/LandingPage.jsx
import BASE_URL from '../config';
import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './LandingPage.css';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// NEW: shared header
import Header from './Header';

import Orb from './Orb';
import faceImg from '../assets/FACE.png';

import assessmentIcon from '../assets/assessment.png';
import analysisIcon from '../assets/analysis.png';
import implementationIcon from '../assets/implementation.png';
import nsfLogo from '../assets/NSF.png';
import nairrLogo from '../assets/nairr_pilot.png';
import purdueLogo from '../assets/Purdue_Logo.png';
import aiModelsImg from '../assets/AI_Models.png';
import marketingImg from '../assets/Marketing_2.png';

export default function LandingPage() {
  const navigate = useNavigate();

  // Scope landing-only CSS to body so footer gap fixes don’t affect other pages
  useEffect(() => {
    document.body.classList.add('landing-scope');
    return () => document.body.classList.remove('landing-scope');
  }, []);

  const handleLaunch = async () => {
    const btn = document.querySelector('.launch-btn');
    if (btn) {
      btn.classList.add('clicked');
      setTimeout(() => btn.classList.remove('clicked'), 1200);
    }

    try {
      const response = await fetch(`${BASE_URL}/verify-session`, {
        method: 'GET',
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        localStorage.setItem('userEmail', data.email);
        navigate('/upload');
      } else {
        const errorData = await response.json();
        toast.error(`Session expired: ${errorData.detail}`);
        setTimeout(() => navigate('/login'), 1500);
      }
    } catch {
      toast.error('Error verifying session. Please login again.');
      setTimeout(() => navigate('/login'), 1500);
    }
  };

  const scrollToInfo = () => {
    document.getElementById('info-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div
      className="landing-container"
      // Reserve space for the fixed header height (Header.css sets --m2-header-h)
      style={{ paddingTop: 'var(--m2-header-h, 72px)' }}
    >
      <ToastContainer />

      {/* NEW: Global gradient header with logo + sign-in */}
      <Header />

      {/* Orb Hero Section */}
      <div className="orb-section">
        <img src={faceImg} alt="Face" className="face-image" />
        <Orb hoverIntensity={0.5} rotateOnHover={true} hue={0} forceHoverState={false} />
        <div className="orb-content">
          <h1 className="hero-title">SynthGuard</h1>
          <p className="hero-subtitle">AI-powered deepfake detection for a safer digital world</p>
          <div className="hero-buttons">
            <button className="gradient-btn launch-btn" onClick={handleLaunch}>
              Launch SynthGuard
            </button>
            <button className="gradient-btn purple" onClick={scrollToInfo}>
              Learn More
            </button>
          </div>
        </div>
      </div>

      {/* Info Section */}
      <section className="info-section" id="info-section">
        <h2 className="info-heading">What is SynthGuard?</h2>
        <p className="info-tagline">A cutting-edge platform for detecting and defending against synthetic media.</p>
        <div className="info-content-box">
          <p>
            <strong>SynthGuard</strong> is an advanced deepfake detection platform built to safeguard the integrity of
            digital media. Combining <em>“Synth”</em> (synthetic) and <em>“Guard”</em> (protection), SynthGuard leverages
            powerful AI models to analyze and detect AI-generated images, videos, and audio.
          </p>
          <p>
            Designed for <strong>speed</strong>, <strong>accuracy</strong>, and <strong>scalability</strong>, the
            platform empowers individuals and organizations to verify the authenticity of digital content and stay
            protected against rising threats from manipulated media.
          </p>
        </div>
      </section>

      {/* Models Image Section */}
      <section className="models-image-section">
        <div className="models-image-box">
          <img src={aiModelsImg} alt="AI Models" className="ai-models-img" />
          <div className="models-description">
            <h3>
              Explore a Wide Range of AI-Powered Forensic Models to Test Your Image for DeepFakes with SynthGuard
            </h3>
            <p>
              From statistical feature extractors to cutting-edge forensic detectors, evaluate your image across
              industry-leading DeepFake detection techniques.
            </p>
          </div>
        </div>
      </section>

      {/* Media Types Section */}
      <section className="media-types-section">
        <div className="media-types-box">
          <img src={marketingImg} alt="Marketing" className="marketing-img" />
          <div className="media-description">
            <h3>Images. Videos. Audios. Detect Every DeepFake with Precision on the SynthGuard Platform</h3>
            <p>No matter the format, our AI-driven forensic tools are built to uncover manipulation and protect authenticity.</p>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="features-section">
        <h2 className="features-heading">Features</h2>
        <div className="feature-cards">
          <div className="feature-card">
            <img src={assessmentIcon} alt="Assessment" className="feature-icon" />
            <h3>Assessment</h3>
            <p>Detect manipulated content using advanced AI and forensics algorithms.</p>
          </div>
          <div className="feature-card">
            <img src={analysisIcon} alt="Analysis" className="feature-icon" />
            <h3>Analysis</h3>
            <p>Gain confidence with detailed predictions and model explainability.</p>
          </div>
          <div className="feature-card">
            <img src={implementationIcon} alt="Scalability" className="feature-icon" />
            <h3>Scalability</h3>
            <p>Built for both real-time detection and batch processing across datasets.</p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-section">
        <h2>Ready to Protect Your Content?</h2>
        <p>Launch SynthGuard and start verifying your media today.</p>
        <button className="get-started-btn" onClick={handleLaunch}>
          Get Started Now
        </button>
      </section>

      {/* Funding */}
      <section className="funding-section">
        <h2 className="funding-title">Funding & Support</h2>
        <div className="funding-logos">
          <img src={nsfLogo} alt="NSF Logo" />
          <img src={nairrLogo} alt="NAIRR Pilot Logo" />
          <img src={purdueLogo} alt="Purdue Logo" />
        </div>
        <p className="funding-text">
          This project is supported by the U.S. National Science Foundation (NSF) under grants <strong>IIS-2434967</strong> and{' '}
          <strong>TI-2448500</strong>, and the <strong>National Artificial Intelligence Research Resource (NAIRR) Pilot</strong>{' '}
          and <strong>TACC Lonestar6</strong>. The views, opinions and/or findings expressed are those of the author and should
          not be interpreted as representing the official views or policies of NSF and NAIRR Pilot.
        </p>
      </section>

      {/* Footer */}
      <footer className="footer-section">
        <div className="footer-left">© 2025 Purdue University</div>
        <div className="footer-center">
          Developed by Machine Learning and Media Forensics (M2) Lab from Purdue University
        </div>
        <div className="footer-right">
          <a href="#">Privacy Policy</a> | <a href="#">Terms of Use</a> | <a href="#">Contact</a>
        </div>
      </footer>
    </div>
  );
}
